import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { LIST_ALL_ATTRIBUTES } from "../../../Constants/Config";
import { coerceServerTimestamp } from "../../../Common/serverTimestamp";
import { toThunkRejectValue } from "../../../Common/isRequestCanceled";
import { ciUrl } from "../../../Constants/nodeCi";

const initialState = {
  loading: false,
  attributesData: [],
  totalAttributes: 0,
  successMessage: "",
  error: "",
  // Infinite scroll: assume there may be more until a short page proves
  // otherwise. Recomputed on every fulfilled fetch from page length vs limit.
  hasMore: true,
};

// Only the latest in-flight list request survives — cancel the previous one
// (shared across fresh load, search, and infinite-scroll append).
let cancelAttributesSource;

// Generate pening , fulfilled and rejected action type
export const fetchAttributesData = createAsyncThunk(
  "attributes/fetchAttributesData.",
  async (data, { rejectWithValue }) => {
    // Cancel the previous in-flight request before firing a new one.
    if (cancelAttributesSource) {
      cancelAttributesSource.cancel("Previous request canceled");
    }
    cancelAttributesSource = axios.CancelToken.source();
    // `silent` and `append` are client-only flags (loading UX + reducer merge
    // mode) — strip them from the request body. `limit`/`offset` stay in and
    // are sent to Varient_react_api/varients_list for pagination.
    const { token, silent: _silent, append: _append, ...dataNew } = data;
    try {
      const response = await axios.post(
        ciUrl(LIST_ALL_ATTRIBUTES),
        dataNew,
        {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        cancelToken: cancelAttributesSource.token,
      });
      if (response.status === 200) {
        const result = Array.isArray(response.data?.result)
          ? response.data.result
          : [];
        // PHP/Node send `total_count` (since a9219ecad); `total` is the old key.
        const totalRaw = Number(
          response.data?.total_count ?? response.data?.total
        );
        const total =
          Number.isFinite(totalRaw) && totalRaw >= 0 ? totalRaw : result.length;
        return { items: result, total };
      }
      return rejectWithValue({
        message: "Unexpected response",
        status: response.status,
      });
    } catch (error) {
      if (axios.isCancel(error)) {
        return rejectWithValue({ cancelled: true });
      }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const attributesSlice = createSlice({
  name: "attributes",
  initialState,
  reducers: {
    editAttribute: (state, action) => {
      const { id, title, updated_timestamp } = action.payload ?? {};
      if (id == null || typeof title !== "string") return;
      if (!Array.isArray(state.attributesData)) {
        state.attributesData = [];
        return;
      }

      const nextTimestamp = coerceServerTimestamp(updated_timestamp);

      state.attributesData = state.attributesData.map((attribute) => {
        if (String(attribute.id) !== String(id)) return attribute;
        const next = {
          ...attribute,
          title,
          old_title: title,
        };
        if (nextTimestamp) {
          next.updated_timestamp = nextTimestamp;
        }
        return next;
      });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchAttributesData.pending, (state, action) => {
      // Only show the full-page skeleton for a fresh/replace load. Appending a
      // page (infinite scroll) or a silent refresh must not blank the list.
      if (!action.meta.arg?.silent && !action.meta.arg?.append) {
        state.loading = true;
      }
    });
    builder.addCase(fetchAttributesData.fulfilled, (state, action) => {
      state.loading = false;
      const payload = action.payload;
      const page = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
          ? payload.items
          : [];
      const apiTotal =
        typeof payload?.total === "number" && payload.total >= 0
          ? payload.total
          : null;
      const { append, limit } = action.meta.arg || {};
      const existing = Array.isArray(state.attributesData)
        ? state.attributesData
        : [];
      if (append) {
        const seen = new Set(existing.map((a) => String(a?.id)));
        const newItems = page.filter((a) => !seen.has(String(a?.id)));
        state.attributesData = [...existing, ...newItems];
      } else {
        state.attributesData = page;
      }
      if (apiTotal != null) {
        state.totalAttributes = apiTotal;
      } else if (!append) {
        state.totalAttributes = page.length;
      }
      const loadedCount = state.attributesData.length;
      // Compare loaded count against API total when available; otherwise
      // infer more pages from whether this page filled the requested limit.
      if (apiTotal != null) {
        state.hasMore = loadedCount < apiTotal;
      } else {
        state.hasMore =
          typeof limit === "number" && limit > 0 ? page.length >= limit : false;
      }
      state.error = "";
    });
    builder.addCase(fetchAttributesData.rejected, (state, action) => {
      // Superseded by a newer request — leave state to that newer request.
      if (action.payload?.cancelled) return;
      state.loading = false;
      // Don't wipe already-loaded pages when an append/silent fetch fails.
      if (!action.meta.arg?.silent && !action.meta.arg?.append) {
        state.attributesData = [];
        state.totalAttributes = 0;
      }
      state.error =
        action.payload?.message || action.error?.message || "Failed to load attributes";
    });

    /* builder.addCase(addToWishlist.pending, (state) => {
            state.loading = true;
        });
        builder.addCase(addToWishlist.fulfilled, (state, action) => {
            state.loading = false;
            state.successMessage = action.payload;
            state.error = ''; // Reset the error message
        });
        builder.addCase(addToWishlist.rejected, (state, action) => {
            state.loading = false;
            state.error = action.error.message;
        });
*/
  },
});

export const { editAttribute } = attributesSlice.actions;
export default attributesSlice.reducer;
